import React from 'react';
import styled from 'styled-components';
import { FiEdit2 } from 'react-icons/fi';
import { ob } from 'components/Onboarding';

/* ── Review form ── */

export const PreviewBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  border: 1px solid rgba(255, 113, 15, 0.25);
  border-left: 4px solid ${ob.primary};
  border-radius: 14px;
  background: rgba(255, 240, 230, 0.55);
  box-shadow: 0 2px 12px rgba(255, 113, 15, 0.08);
  @media (max-width: 540px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
  }
  .text {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${ob.primary};
  }
  .heading {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
  }
  .sub {
    margin-top: 1px;
    font-size: 12.5px;
    color: ${ob.muted};
  }
`;

export const PillButton = styled.button<{ $dark?: boolean; $block?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  height: 42px;
  padding: 0 22px;
  border: 0;
  border-radius: 999px;
  background: ${ob.primary};
  color: #fff;
  box-shadow: ${ob.shadowBrand};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    filter: brightness(0.96);
  }
  @media (max-width: 540px) {
    width: 100%;
  }
`;

export const SectionCard = styled.section`
  padding: 20px 24px;
  border: 1px solid ${ob.border};
  border-radius: 16px;
  background: ${ob.card};
  box-shadow: 0 1px 2px rgba(26, 26, 26, 0.04);
  @media (max-width: 540px) {
    padding: 16px;
  }
`;

const SectionHeadWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${ob.border};
  h2 {
    margin: 0;
    font-family: ${ob.fontDisplay};
    font-size: 15px;
    font-weight: 600;
  }
`;

const EditButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid ${ob.border};
  border-radius: 999px;
  background: #fff;
  color: ${ob.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 140ms ease;
  &:hover {
    border-color: rgba(255, 113, 15, 0.45);
    background: ${ob.primarySoft};
  }
  svg {
    color: ${ob.primary};
  }
`;

export const SectionHead: React.FC<{ title: React.ReactNode; onEdit?: () => void }> = ({
  title,
  onEdit,
}) => (
  <SectionHeadWrap>
    <h2>{title}</h2>
    {onEdit ? (
      <EditButton type="button" onClick={onEdit}>
        <FiEdit2 size={14} aria-hidden /> Edit
      </EditButton>
    ) : null}
  </SectionHeadWrap>
);

const RowWrap = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 9px 0;
  border-bottom: 1px solid ${ob.border};
  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
  .label {
    min-width: 160px;
    flex-shrink: 0;
    font-size: 13px;
    font-weight: 600;
    color: ${ob.muted};
  }
  .value {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
  }
  .value a {
    color: ${ob.primary};
    text-decoration: none;
    word-break: break-all;
  }
  .value a:hover {
    text-decoration: underline;
  }
  @media (max-width: 480px) {
    align-items: flex-start;
    gap: 10px 12px;
    .label {
      min-width: 0;
      flex: 0 1 42%;
      max-width: 152px;
      font-size: 12px;
      line-height: 1.35;
    }
    .value {
      font-size: 13px;
      line-height: 1.45;
    }
  }
`;

export const Row: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <RowWrap>
    <span className="label">{label}</span>
    <span className="value">{children}</span>
  </RowWrap>
);

/** Rich-text description (TipTap HTML) shown in the review. */
export const DescriptionHtml = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 1.5;
  p {
    margin: 0 0 0.5em;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0.35em 0;
    padding-left: 1.25em;
  }
  a {
    color: ${ob.primary};
    word-break: break-all;
  }
`;

export const TagGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 4px;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid ${ob.border};
  border-radius: 999px;
  background: #fff;
  font-size: 13px;
  color: ${ob.text};
`;

export const SubHeading = styled.p`
  margin: 16px 0 8px;
  padding-top: 12px;
  border-top: 1px solid ${ob.border};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${ob.muted};
  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
  }
`;

export const PhotoCategory = styled.div`
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
  .label {
    margin-bottom: 10px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${ob.muted};
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    @media (max-width: 480px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .card {
    position: relative;
    overflow: hidden;
    aspect-ratio: 4 / 3;
    border: 1px solid ${ob.border};
    border-radius: 12px;
    background: ${ob.mutedBg};
  }
  .card img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .badge {
    position: absolute;
    top: 6px;
    left: 6px;
    padding: 3px 8px;
    border-radius: 999px;
    background: ${ob.primary};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`;

export const Muted = styled.span`
  color: ${ob.muted};
`;

export const EmptyNote = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${ob.muted};
`;

export const StatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 4px;
  .chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
  }
  .available {
    background: ${ob.successSoft};
    color: ${ob.success};
  }
  .blocked {
    background: rgba(190, 18, 60, 0.08);
    color: ${ob.danger};
  }
  .booked {
    background: #eff6ff;
    color: #3182ce;
  }
`;

export const ConfirmSection = styled.div`
  padding: 20px 24px;
  border: 1px solid ${ob.border};
  border-radius: 16px;
  background: ${ob.surface};
  .row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 3px;
    accent-color: ${ob.primary};
    cursor: pointer;
  }
  label {
    font-size: 14px;
    line-height: 1.6;
    color: ${ob.text};
    cursor: pointer;
  }
  a {
    color: ${ob.primary};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

export const FloatingPreview = styled.button<{ $hidden?: boolean; $lift?: boolean }>`
  position: fixed;
  bottom: ${({ $lift }) => ($lift ? '96px' : '28px')};
  left: 50%;
  z-index: 35;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 26px;
  border: 0;
  border-radius: 999px;
  background: ${ob.text};
  color: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  transform: translateX(-50%) ${({ $hidden }) => ($hidden ? 'translateY(12px)' : 'none')};
  &:hover {
    background: #333;
  }
`;

/* ── Success state ── */

export const SuccessWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 620px;
  margin: 0 auto;
  padding: 40px 0 80px;
  text-align: center;
  .icon {
    width: 110px;
    height: 110px;
    margin-bottom: 24px;
  }
  h1 {
    margin: 0 0 12px;
    font-family: ${ob.fontDisplay};
    font-size: clamp(22px, 4vw, 30px);
    font-weight: 700;
    line-height: 1.3;
  }
  > p {
    max-width: 440px;
    margin: 0 0 28px;
    font-size: 15px;
    line-height: 1.7;
    color: ${ob.muted};
  }
`;

export const PropertyCardBox = styled.div`
  width: 100%;
  margin-bottom: 24px;
  overflow: hidden;
  border: 1px solid ${ob.border};
  border-radius: 20px;
  background: #fff;
  box-shadow: ${ob.shadowCard};
  text-align: left;
  .thumb,
  .placeholder {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
    background: ${ob.mutedBg};
  }
  .placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
  }
  .body {
    padding: 20px 22px 22px;
  }
  .eyebrow {
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${ob.muted};
  }
  .title {
    margin-bottom: 6px;
    font-family: ${ob.fontDisplay};
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 4px;
    font-size: 13.5px;
    color: ${ob.muted};
  }
  .divider {
    width: 100%;
    height: 1px;
    margin: 16px 0;
    background: ${ob.border};
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    > * {
      flex: 1;
      min-width: 140px;
    }
  }
`;

export const TextLinkButton = styled.button`
  padding: 12px 28px;
  border: 0;
  background: transparent;
  color: ${ob.muted};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  &:hover {
    color: ${ob.text};
  }
`;

/* ── Cancellation policy rows ── */

export const PolicyRows = styled.div`
  display: flex;
  flex-direction: column;
  .r {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid ${ob.border};
  }
  .r:last-child {
    border-bottom: 0;
  }
  .l {
    flex: 1;
    font-size: 13px;
    line-height: 1.4;
  }
  .tag {
    flex-shrink: 0;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }
`;
